import { DataTableColumnHeader } from '@/components/dataTable/ColumnHeader';
import { getSerialNumber } from '@/components/dataTable/data-table.utils';
import { formatDate } from '@/lib/dateFormatter';
import { formatAmount } from '@/lib/format';
import { cn } from '@/lib/utils';
import { AffiliateReferralDetail } from '@/services/dashboard/referralReports/referralReports.types';
import type { ColumnDef } from '@tanstack/react-table';
import { getReferralTypeLabel } from '../../utils/getReferralTypeLabel';

const textCell = (value: string | number) => (
  <div className="whitespace-nowrap text-xs font-medium text-foreground-detail">{value}</div>
);

export const getAffiliateNetworkColumns = (t: (key: string) => string): ColumnDef<AffiliateReferralDetail>[] => [
  {
    accessorKey: 'sn',
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.sn')} />,
    cell: ({ row, table }) => textCell(getSerialNumber(table, row.index)),
    enableSorting: false,
    enableHiding: false,
  },
  {
    accessorKey: 'affiliateInfo',
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.userInfo')} />,
    cell: ({ row }) => (
      <div className="flex flex-col gap-1 whitespace-nowrap">
        <div className="block max-w-[15rem] truncate text-xs font-medium">{row.getValue<string>('affiliateInfo')}</div>
        <span className="text-xs font-medium text-foreground-subtle">{row.original.emailAddress}</span>
      </div>
    ),
  },
  {
    accessorKey: 'reservedBy',
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.reservedBy')} />,
    cell: ({ row }) => textCell(row.getValue<string>('reservedBy')),
  },
  {
    accessorKey: 'referralType',
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.referralType')} />,
    cell: ({ row }) => {
      const relationship = getReferralTypeLabel(row.getValue<string>('referralType'), t);
      const referralType = row.getValue<string>('referralType')?.trim().toLowerCase();

      return (
        <div
          className={cn('whitespace-nowrap text-xs font-medium', {
            'text-success': referralType === 'direct',
            'text-referral-indirect': referralType === 'indirect',
            'text-muted-foreground': referralType !== 'direct' && referralType !== 'indirect',
          })}
        >
          {t('common.page.table.affiliateIntroduction')} ({relationship})
        </div>
      );
    },
  },
  {
    id: 'createdDate',
    accessorFn: (row) => row.visitDateTime,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('common.page.tableColumn.visitDateTime')} />
    ),
    cell: ({ row }) => textCell(formatDate(row.getValue<number>('createdDate'), 'YYYY/MM/DD HH:mm:ss')),
    enableColumnFilter: false,
  },
  {
    accessorKey: 'rewardPoints',
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('common.page.tableColumn.rewardPoints')} />,
    cell: ({ row }) => (
      <div className="whitespace-nowrap text-right text-xs font-medium text-foreground-detail">
        {formatAmount(row.getValue<number>('rewardPoints'), ' ')} 円
      </div>
    ),
    meta: { align: 'right' },
  },
];
